import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import PaymentPage from './PaymentPage';
import Confirming from './Confirming';
import Success from './Success';
import Cancel from './Cancel';
import Donations from './Donations';
import { CreditCard } from 'lucide-react';
import './App.css';

function DonationPaymentPage(props) {
  const [amount, setAmount] = useState(5);

  useEffect(() => {
    const saved = localStorage.getItem('donationAmount');
    if (saved) {
      const numAmount = parseFloat(saved);
      if (numAmount >= 1 && numAmount <= 10000) {
        setAmount(numAmount);
      }
    }
  }, []);

  return (
    <PaymentPage
      {...props}
      price={amount}
      productName="Donación"
      handleCryptoPayment={() => props.handleCryptoPayment(amount, 'Donación')}
    />
  );
}

// 🔥 Wrapper para precio variable (lee ?price=... del query, fallback al precio base)
// Usado por servicios con extras opcionales (p.ej. Crea tu LLC con EIN Express / web).
function VariablePricePage({ basePrice, productName, allowedMin, allowedMax, ...props }) {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('price');
  let price = basePrice;
  if (raw) {
    const n = parseFloat(raw);
    const min = typeof allowedMin === 'number' ? allowedMin : basePrice;
    const max = typeof allowedMax === 'number' ? allowedMax : basePrice + 500;
    if (!Number.isNaN(n) && n >= min && n <= max) price = n;
  }
  return (
    <PaymentPage
      {...props}
      price={price}
      productName={productName}
      handleCryptoPayment={() => props.handleCryptoPayment(price, productName)}
    />
  );
}

function App() {
  const [step, setStep] = useState('payment');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const [isFiatMode, setIsFiatMode] = useState(true);
  const [customerEmail, setCustomerEmail] = useState('');
  // 🔥 TTL alineado con el backend (2h). Ver comentario en PaymentPage timer.
  const [timeLeft, setTimeLeft] = useState(2 * 60 * 60);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // 🔥 Redes principales (siempre visibles)
  // - 'card' es siempre fiat. 'apple_pay' y 'google_pay' se muestran condicionalmente según
  //   detección de PaymentRequest API en PaymentPage.js (no aparecen en desktop sin wallet
  //   ni en navegador in-app de Telegram).
  // - Por defecto solo se muestra USDT TRC20. El resto de redes/monedas en "Más Redes".
  const paymentMethods = [
    { id: 'card', name: 'Tarjeta', network: 'Débito / Crédito', icon: <CreditCard className="w-6 h-6" />, isFiat: true },
    { id: 'apple_pay', name: 'Apple Pay', network: 'Pago rápido', isFiat: true, isWallet: true, walletKind: 'apple' },
    { id: 'google_pay', name: 'Google Pay', network: 'Pago rápido', isFiat: true, isWallet: true, walletKind: 'google' },
    { id: 'usdttrc20', name: 'USDT', network: 'TRC20', symbol: '₮', color: '#F3192F' }
  ];

  // 🔥 Redes adicionales (se muestran al hacer clic en "Más Redes")
  const extraPaymentMethods = [
    { id: 'usdtsol', name: 'USDT', network: 'Solana', symbol: '₮', color: '#32C9B8' },
    { id: 'usdtbsc', name: 'USDT', network: 'BSC (BEP20)', symbol: '₮', color: '#F3BA2F' },
    { id: 'usdtpol', name: 'USDT', network: 'Polygon', symbol: '₮', color: '#6C00F7' },
    { id: 'usdterc20', name: 'USDT', network: 'Ethereum', symbol: '₮', color: '#627EEA' },
    { id: 'usdcsol', name: 'USDC', network: 'Solana', symbol: '$', color: '#2775CA' },
    { id: 'usdcbsc', name: 'USDC', network: 'BSC (BEP20)', symbol: '$', color: '#F3BA2F' },
    { id: 'usdcpol', name: 'USDC', network: 'Polygon', symbol: '$', color: '#6C00F7' },
    { id: 'usdcerc20', name: 'USDC', network: 'Ethereum', symbol: '$', color: '#627EEA' }
  ];

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const goBack = () => {
    setStep('payment');
    setPaymentInfo(null);
    setError(null);
    setIsProcessing(false);
    setSelectedCrypto('usdttrc20');
    setIsFiatMode(false);
    setTimeLeft(2 * 60 * 60);
    localStorage.removeItem('currentPayment');
  };

  const handleCryptoPayment = async (price, productName) => {
    if (isProcessing) return;

    // Trim del email para evitar falsos negativos al copiar-pegar con espacios.
    const cleanEmail = (customerEmail || '').trim();

    if (!cleanEmail) {
      setError('El email es obligatorio');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError('Introduce un email válido');
      return;
    }

    if (!selectedCrypto) {
      setError('Selecciona una red de pago');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setStep('processing');

    // 🔥 AbortController con timeout 30s. Defensa contra navegadores in-app (Telegram,
    // Instagram, FB) o extensiones de privacidad que cuelgan requests cross-origin a
    // *.railway.app. Sin esto el spinner gira indefinidamente y el cliente no sabe qué
    // pasa. Las respuestas reales del backend vienen <2s; 30s es margen amplio.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      console.log('🚀 Creando pago...');

      // Recoger extras del formulario si vinieron (Crea LLC con extras)
      let extrasForBackendC = null;
      let basePriceForBackendC = null;
      try {
        const raw = sessionStorage.getItem('pendingExtras');
        if (raw) {
          const parsed = JSON.parse(raw);
          extrasForBackendC = parsed.extras_for_portal || null;
          basePriceForBackendC = parsed.base_price ?? null;
        }
      } catch (e) {}

      let formDataToSendC = null;
      try {
        const rawFd = localStorage.getItem('pendingFormData');
        if (rawFd) {
          formDataToSendC = JSON.parse(rawFd);
          // 🔥 Restaurar archivos base64 desde sessionStorage
          try {
            const pendingFiles = sessionStorage.getItem('pendingUploadFiles');
            if (pendingFiles) {
              const filesMap = JSON.parse(pendingFiles);
              for (const [fieldId, files] of Object.entries(filesMap)) {
                if (formDataToSendC.answers && Array.isArray(files) && files.length > 0) {
                  formDataToSendC.answers[fieldId] = files;
                }
              }
            }
          } catch (e) {}
        }
      } catch (e) {}

      const response = await fetch(`${BACKEND_URL}/api/create-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          price_amount: price,
          pay_currency: selectedCrypto,
          customer_email: cleanEmail,
          product_name: productName,
          order_id: `ORDER_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
          ...(extrasForBackendC && extrasForBackendC.length ? { extras_for_portal: extrasForBackendC } : {}),
          ...(basePriceForBackendC != null ? { base_price: basePriceForBackendC } : {}),
          ...(formDataToSendC ? { form_data: formDataToSendC } : {})
        })
      });

      if (!response.ok) {
        // Intentar leer el mensaje específico del backend (devuelve { error: "..." })
        let serverMsg = `Error del servidor (HTTP ${response.status})`;
        try {
          const errData = await response.json();
          if (errData?.error) serverMsg = errData.error;
        } catch (e) {}
        throw new Error(serverMsg);
      }

      const data = await response.json();

      if (!data.pay_address) {
        throw new Error('No se recibió dirección de pago');
      }

      let qrUrl = '';
      const networkLower = selectedCrypto.toLowerCase();
      
      if (networkLower.includes('trc20') || networkLower.includes('trx')) {
        qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=tron:${data.pay_address}?amount=${data.pay_amount}`;
      } else if (networkLower.includes('sol')) {
        qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=solana:${data.pay_address}?amount=${data.pay_amount}`;
      } else if (networkLower.includes('bsc') || networkLower.includes('bep20')) {
        qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=bsc:${data.pay_address}?amount=${data.pay_amount}`;
      } else if (networkLower.includes('pol') || networkLower.includes('matic')) {
        qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=ethereum:${data.pay_address}?amount=${data.pay_amount}`;
      } else if (networkLower.includes('erc20') || networkLower.includes('eth')) {
        qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=ethereum:${data.pay_address}?amount=${data.pay_amount}`;
      } else {
        qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${data.pay_address}`;
      }

      const paymentData = {
        orderId: data.payment_id || data.order_id,
        address: data.pay_address,
        amount: data.pay_amount,
        currency: data.pay_currency,
        qrCode: qrUrl,
        selectedCrypto: selectedCrypto,
        customerEmail: cleanEmail,
        timeLeft: 2 * 60 * 60,
        step: 'showing-address'
      };

      setPaymentInfo(paymentData);
      localStorage.setItem('currentPayment', JSON.stringify(paymentData));
      setStep('showing-address');
      setTimeLeft(2 * 60 * 60);

    } catch (err) {
      console.error('❌ Error:', err);
      // Distinguir timeout (AbortError) vs fallo de red (TypeError) vs error explícito
      // del servidor. Esto da al cliente una pista accionable, sobre todo en navegadores
      // in-app de Telegram/Instagram/FB donde algunos requests fallan silenciosamente.
      if (err.name === 'AbortError') {
        setError('La conexión al servidor está tardando demasiado. Si estás en el navegador de Telegram, copia el enlace y ábrelo en Chrome o Safari. También prueba con otra red.');
      } else if (err instanceof TypeError) {
        setError('No se pudo conectar con el servidor. Verifica tu red o desactiva extensiones/bloqueadores e inténtalo de nuevo.');
      } else {
        setError(err.message || 'Error al generar dirección de pago. Por favor, intenta de nuevo.');
      }
      setStep('payment');
    } finally {
      clearTimeout(timeoutId);
      setIsProcessing(false);
    }
  };

  const commonProps = {
    step, setStep,
    isProcessing, setIsProcessing,
    error, setError,
    paymentInfo, setPaymentInfo,
    copied, setCopied,
    selectedCrypto, setSelectedCrypto,
    customerEmail, setCustomerEmail,
    timeLeft, setTimeLeft,
    formatTime,
    copyToClipboard,
    goBack,
    paymentMethods,
    extraPaymentMethods,
    isFiatMode, setIsFiatMode
  };

  return (
    <Router>
      <Routes>
        
        {/* ============================================
            🔥 ASESORÍAS
        ============================================ */}
        
        {/* Asesoría Estándar - $119 (calendario gestionado por forms-asesorias.html) */}
        <Route path="/asesoria-estandar" element={
          <PaymentPage 
            {...commonProps} 
            price={119} 
            productName="Asesoría Estándar" 
            requiresCalendar={false}
            handleCryptoPayment={() => handleCryptoPayment(119, 'Asesoría Estándar')} 
          />
        }/>

        {/* Asesoría Pro - $449 */}
        <Route path="/asesoria-pro" element={
          <PaymentPage 
            {...commonProps} 
            price={449} 
            productName="Asesoría Pro" 
            requiresCalendar={false}
            handleCryptoPayment={() => handleCryptoPayment(449, 'Asesoría Pro')} 
          />
        }/>

        {/* Asesoría Avanzada - $529 */}
        <Route path="/asesoria-avanzada" element={
          <PaymentPage 
            {...commonProps} 
            price={529} 
            productName="Asesoría Avanzada" 
            requiresCalendar={false}
            handleCryptoPayment={() => handleCryptoPayment(529, 'Asesoría Avanzada')} 
          />
        }/>

        {/* Asesoría 45 min (legacy) */}
        <Route path="/asesoria-45" element={
          <PaymentPage 
            {...commonProps} 
            price={119} 
            productName="Asesoría 45 minutos" 
            requiresCalendar={true}
            handleCryptoPayment={() => handleCryptoPayment(119, 'Asesoría 45 min')} 
          />
        }/>

        {/* ============================================
            🔥 SERVICIOS LLC
        ============================================ */}

        {/* Crea tu LLC - $650 base. Extras: EIN Express +$100, Dirección Física USA +$350, Forms anuales +$230, Web +$100 (+$780)
            + Recargo por estado: WYOMING +$49 / DELAWARE +$58 / FLORIDA +$73 → máx $1503 (margen a $1550) */}
        <Route path="/crea-tu-llc" element={
          <VariablePricePage
            {...commonProps}
            basePrice={650}
            allowedMin={650}
            allowedMax={1550}
            productName="Crea tu LLC"
            handleCryptoPayment={handleCryptoPayment}
          />
        }/>

        {/* Cierra tu LLC - $340 */}
        <Route path="/cierra-tu-llc" element={
          <PaymentPage 
            {...commonProps} 
            price={340} 
            productName="Cierra tu LLC" 
            handleCryptoPayment={() => handleCryptoPayment(340, 'Cierra tu LLC')} 
          />
        }/>

        {/* Cierre de OÜ en Estonia - $1650 */}
        <Route path="/cierra-tu-ou" element={
          <PaymentPage
            {...commonProps}
            price={1650}
            productName="Cierre de OÜ en Estonia"
            handleCryptoPayment={() => handleCryptoPayment(1650, 'Cierre de OÜ en Estonia')}
          />
        }/>

        {/* ============================================
            🔥 SERVICIOS FISCALES
        ============================================ */}

        {/* EIN - $230 */}
        <Route path="/ein" element={
          <PaymentPage 
            {...commonProps} 
            price={230} 
            productName="EIN" 
            handleCryptoPayment={() => handleCryptoPayment(230, 'EIN')} 
          />
        }/>

        {/* EIN Express - $349 */}
        <Route path="/ein-express" element={
          <PaymentPage 
            {...commonProps} 
            price={349} 
            productName="EIN Express" 
            handleCryptoPayment={() => handleCryptoPayment(349, 'EIN Express')} 
          />
        }/>

        {/* ITIN - $395 */}
        <Route path="/itin" element={
          <PaymentPage 
            {...commonProps} 
            price={395} 
            productName="ITIN" 
            handleCryptoPayment={() => handleCryptoPayment(395, 'ITIN')} 
          />
        }/>

        {/* FORMS ANUALES - $229 */}
        <Route path="/forms" element={
          <PaymentPage
            {...commonProps}
            price={229}
            productName="FORMS ANUALES (5472 Y 1120)"
            handleCryptoPayment={() => handleCryptoPayment(229, 'FORMS ANUALES (5472 Y 1120)')}
          />
        }/>

        {/* RELAY - Cuenta Pre Aprobada - $25 */}
        <Route path="/relay" element={
          <PaymentPage
            {...commonProps}
            price={25}
            productName="Cuenta Pre Aprobada Relay"
            handleCryptoPayment={() => handleCryptoPayment(25, 'Cuenta Pre Aprobada Relay')}
          />
        }/>

        {/* ============================================
            🔥 DONACIONES
        ============================================ */}
        
        <Route path="/donaciones" element={<Donations />} />

        <Route path="/pago-donacion" element={
          <DonationPaymentPage {...commonProps} handleCryptoPayment={handleCryptoPayment} />
        }/>

        {/* ============================================
            🔥 RUTAS LEGACY (mantener por compatibilidad)
        ============================================ */}
        
        <Route path="/pago-premium-5" element={
          <PaymentPage 
            {...commonProps} 
            price={5} 
            productName="Premium Package" 
            handleCryptoPayment={() => handleCryptoPayment(5, 'Premium Package')} 
          />
        }/>
        
        <Route path="/pago-basic-10" element={
          <PaymentPage 
            {...commonProps} 
            price={10} 
            productName="Basic Service" 
            handleCryptoPayment={() => handleCryptoPayment(10, 'Basic Service')}  
          />
        }/>
        
        <Route path="/pago-pro-15" element={
          <PaymentPage 
            {...commonProps} 
            price={15} 
            productName="Pro Package" 
            handleCryptoPayment={() => handleCryptoPayment(15, 'Pro Package')} 
          />
        }/>
        
        <Route path="/pago-top-50" element={
          <PaymentPage 
            {...commonProps} 
            price={50} 
            productName="Top Services" 
            handleCryptoPayment={() => handleCryptoPayment(50, 'Top Services')}
          />
        }/>
        
        <Route path="/pago-vip-50" element={
          <PaymentPage 
            {...commonProps} 
            price={50} 
            productName="VIP Access" 
            handleCryptoPayment={() => handleCryptoPayment(50, 'VIP Access')} 
          />
        }/>

        {/* ============================================
            Rutas del sistema (no modificar)
        ============================================ */}
        <Route path="/confirming" element={<Confirming />} />
        <Route path="/success" element={<Success />} />
        <Route path="/cancel" element={<Cancel />} />

        {/* Ruta por defecto */}
        <Route path="*" element={
          <PaymentPage 
            {...commonProps} 
            price={5} 
            productName="Premium Package" 
            handleCryptoPayment={() => handleCryptoPayment(5, 'Premium Package')} 
          />
        }/>
      </Routes>
    </Router>
  );
}

export default App;
